import cookie from 'cookie';
import { defined } from '../defined.js';
import { empty, slash } from '../strings.js';
import { supportsCookie, supportsSession } from './browser-support.js';
import { reusable } from './convincely.js';
/** Storage base class **/
export class DataStorage {
    /**
     * @param {string}  [prefix]
     * @param {boolean} [autoClear]
     * @param {object}  [data]
     * @param {DataStorage} [secondary] - a secondary backup storage
     */
    constructor(prefix, autoClear = false, data = {}, secondary) {
        this._data = data;
        this.keys = Object.keys(data);
        this.prefix = prefix;
        if (secondary) {
            this.secondary = secondary;
        }
        if (autoClear) {
            storages.push(this);
        }
    }
    /**
     * @abstract
     * @param {string} key
     * @param {*} value
     * @return {boolean} true when value is defined
     */
    setItem(key, value) {
        if (!defined(value)) {
            return false;
        }
        this._data[key] = value;
        if (this.secondary) {
            this.secondary.setItem(key, value);
        }
        return true;
    }
    /**
     * @abstract
     * @param {string} key
     * @return {boolean} always true
     */
    removeItem(key) {
        delete this._data[key];
        if (this.secondary) {
            this.secondary.removeItem(key);
        }
        return true;
    }
    /**
     * clears the storage
     */
    clear() {
        for (const key of this.keys) {
            this.removeItem(key);
        }
        this.keys = [];
    }
}
const slashes = /\//g;
const slashesReplace = '~~';
const slashesReplaceRegex = /~~/g;
export class CookieStorage extends DataStorage {
    constructor(prefix = empty, autoClear = false, data = {}, options) {
        const values = cookie.parse(document.cookie);
        for (const name of Object.keys(values)) {
            values[name] = values[name].replace(slashesReplaceRegex, slash);
        }
        super(prefix, autoClear, dataByPrefix(data, values, prefix));
        const cookieOptions = { path: slash, sameSite: 'strict' };
        this.options = options ? Object.assign(options, cookieOptions, options) : cookieOptions;
        this.removeOptions = { ...this.options, maxAge: -1, expires: new Date(0) };
    }
    sync() {
        dataByPrefix(this._data, cookie.parse(document.cookie), this.prefix);
    }
    setItem(key, value) {
        const valid = super.setItem(key, value);
        if (valid) {
            const encodedValue = toValue(value);
            const replacedValue = typeof encodedValue === 'string'
                ? encodedValue.replace(slashes, slashesReplace)
                : encodedValue;
            document.cookie = cookie.serialize(this.prefix + key, replacedValue, this.options);
        }
        return valid;
    }
    removeItem(key) {
        const valid = super.removeItem(key);
        if (valid) {
            document.cookie = cookie.serialize(this.prefix + key, empty, this.removeOptions);
        }
        return valid;
    }
}
export class SessionStorage extends DataStorage {
    constructor(prefix = empty, autoClear = false, data = {}, secondary, storage = window.sessionStorage) {
        super(prefix, autoClear, dataByPrefix(data, storage, prefix), secondary);
        this.storage = storage;
    }
    setItem(key, value) {
        const valid = super.setItem(key, value);
        if (valid) {
            this.storage[this.prefix + key] = toValue(value);
        }
        return valid;
    }
    removeItem(key) {
        const valid = super.removeItem(key);
        if (super.removeItem(key)) {
            this.storage.removeItem(this.prefix + key);
        }
        return valid;
    }
}
export const storages = reusable('storages', []);
export const cookiesStorage = new CookieStorage();
/**
 * Wrapper for cookies data that might be deferred
 *
 * @type {{readonly data: Object.<string, string>}}
 */
export const cookies = {
    get data() {
        return dataByPrefix({}, cookie.parse(document.cookie));
    },
};
export const session = supportsSession ? sessionStorage : cookiesStorage;
export const clearSession = reusable('clearSession', () => {
    storages.forEach((item) => {
        item.clear();
    });
});
/**
 * @param {string} key
 * @param {object} [initialData]
 * @param {object} [cookieOptions]
 * @param {boolean} [useCookies]
 * @return {DataStorage}
 */
export function createStorage(key, initialData, cookieOptions, useCookies = true) {
    const prefix = `${key}_`;
    if (useCookies) {
        if (!supportsCookie) {
            return new DataStorage(prefix, false, initialData);
        }
        const cookieStorage = new CookieStorage(prefix, true, initialData, cookieOptions);
        cookieStorage.cookieStorage = cookieStorage;
        if (!supportsSession) {
            return cookieStorage;
        }
        const sessionStorage = new SessionStorage(prefix, true, initialData, cookieStorage);
        sessionStorage.cookieStorage = cookieStorage;
        if (cookieStorage.keys.length > sessionStorage.keys.length) {
            delete sessionStorage.secondary;
            cookieStorage.secondary = sessionStorage;
            return cookieStorage;
        }
        return sessionStorage;
    }
    else {
        const secondaryStorage = new DataStorage(prefix, false, initialData);
        if (!supportsSession) {
            return secondaryStorage;
        }
        return new SessionStorage(prefix, true, initialData, secondaryStorage);
    }
}
/**
 * @param {string} key
 * @param {object} [initialData]
 * @return {DataStorage}
 */
export function createSessionStorage(key, initialData) {
    const prefix = `${key}_`;
    if (!supportsSession) {
        return new DataStorage(prefix, true, initialData);
    }
    return new SessionStorage(prefix, true, initialData);
}
/**
 * @param {string} key
 * @param {object} [initialData]
 * @param {object} [cookieOptions]
 * @return {DataStorage}
 */
export function createCookieStorage(key, initialData, cookieOptions) {
    const prefix = `${key}_`;
    if (!supportsCookie) {
        return new DataStorage(prefix, true, initialData);
    }
    return new CookieStorage(prefix, true, initialData, cookieOptions);
}
/**
 * @param {T} data
 * @template T
 * @return {object|T}
 */
export function fromValue(data) {
    if (typeof data === 'string' && data.length > 1 && data[0] === '0') {
        return data;
    }
    try {
        return JSON.parse(data);
    }
    catch (e) {
        return data;
    }
}
/**
 * @param {T} data
 * @template T
 * @return {string|T}
 */
function toValue(data) {
    return typeof data === 'object' ? JSON.stringify(data) : data;
}
/**
 * @param {T} data
 * @param {*} storage
 * @param {string} [prefix]
 * @template T
 * @return {T}
 */
function dataByPrefix(data, storage, prefix) {
    Object.keys(storage)
        .filter((key) => !prefix || key.indexOf(prefix) === 0)
        .forEach((key) => {
        const rawKey = prefix ? key.substr(prefix.length) : key;
        data[rawKey] = fromValue(storage[key]);
    });
    return data;
}
//# sourceMappingURL=storage.js.map